import { useId, useRef, useState } from "react";
import { createNewsLink, newsLinkHref } from "../../utils/newsLinks";
import { Button, Field, Notice } from "./components/AdminUI";
import styles from "./admin.module.css";

export function NewsBodyEditor({ value, onChange, disabled = false }) {
  const textarea = useRef(null);
  const selection = useRef({ start: 0, end: 0 });
  const panelId = useId();
  const [link, setLink] = useState(null);
  const [error, setError] = useState("");

  const openLink = () => {
    const start = textarea.current?.selectionStart ?? value.length;
    const end = textarea.current?.selectionEnd ?? start;
    selection.current = { start, end };
    const selected = value.slice(start, end);
    setLink({ label: selected.replace(/\s+/g, " "), url: newsLinkHref(selected) || "" });
    setError("");
  };
  const restoreFocus = (position) => {
    requestAnimationFrame(() => {
      textarea.current?.focus();
      if (position !== undefined) textarea.current?.setSelectionRange(position, position);
    });
  };
  const insertLink = () => {
    const href = newsLinkHref(link.url);
    if (!href) {
      setError("Укажите адрес сайта, начинающийся с https://, http:// или www.");
      return;
    }
    const { start, end } = selection.current;
    const inserted = createNewsLink(link.label, href);
    const next = value.slice(0, start) + inserted + value.slice(end);
    if (next.length > 50000) {
      setError("После вставки текст превысит 50 000 символов. Сократите его и попробуйте снова.");
      return;
    }
    onChange(next);
    setLink(null);
    setError("");
    restoreFocus(start + inserted.length);
  };
  return (
    <div className={styles.newsBodyEditor}>
      <div className={styles.newsTextToolbar}>
        <Button
          icon="external"
          onClick={openLink}
          disabled={disabled || Boolean(link)}
          aria-expanded={Boolean(link)}
          aria-controls={link ? panelId : undefined}
        >
          Добавить ссылку
        </Button>
        <span className={styles.hint}>Можно сначала выделить слова в тексте.</span>
      </div>
      {link && (
        <div
          id={panelId}
          className={styles.newsLinkPanel}
          role="group"
          aria-label="Вставка ссылки"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              setLink(null);
              restoreFocus();
            } else if (event.key === "Enter" && event.target.tagName === "INPUT") {
              event.preventDefault();
              event.stopPropagation();
              insertLink();
            }
          }}
        >
          <Field
            label="Текст ссылки"
            value={link.label}
            onChange={(event) => setLink({ ...link, label: event.target.value })}
            placeholder="Например: Подключиться к встрече"
            maxLength={500}
            hint="Если оставить пустым, покажем сам адрес."
          />
          <Field
            label="Адрес ссылки"
            value={link.url}
            onChange={(event) => { setLink({ ...link, url: event.target.value }); setError(""); }}
            placeholder="https://…"
            inputMode="url"
            autoFocus
            maxLength={2048}
          />
          <Notice danger>{error}</Notice>
          <div className={styles.newsLinkActions}>
            <Button onClick={() => { setLink(null); restoreFocus(); }}>Отменить вставку</Button>
            <Button variant="primary" onClick={insertLink}>Вставить ссылку</Button>
          </div>
        </div>
      )}
      <Field
        ref={textarea}
        label="Текст новости"
        type="textarea"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        readOnly={Boolean(link)}
        required
        minLength={20}
        maxLength={50000}
        rows={14}
        hint="Адреса https://… и www.… станут кликабельными автоматически. Ссылка с подписью записывается как [текст](https://…). Отделяйте абзацы пустой строкой."
        placeholder="Расскажите подробности…"
      />
    </div>
  );
}
